import { useState, useEffect } from "react"
import { Monitor, X, Check, Clock, Loader2, Files, Video, Presentation, PenLine } from "lucide-react"
import { Button } from "@/clientWeb/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/clientWeb/components/ui/dialog"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"

type DialogState = "request" | "pending" | "approved" | "rejected"

/**
 * What the asker is trying to do. All three need the same thing — presenter —
 * so they share this flow, but a dialog that says "screen share" to someone who
 * clicked Document Share reads as the wrong dialog and gets dismissed.
 */
export type PresenterRequestSubject = "screenShare" | "document" | "video" | "whiteboard"

/**
 * Everything this dialog can ask for. Annotation is not presenter: it is its
 * own grant (`annotation:request`), asked from the pen over a document, but the
 * ask, the wait and the answer look the same.
 */
export type AccessRequestSubject = PresenterRequestSubject | "annotation"

export const ACCESS_REQUEST_COPY: Record<
  AccessRequestSubject,
  {
    icon: typeof Monitor
    title: string
    need: string
    waiting: string
    granted: string
    denied: string
    start: string
  }
> = {
  screenShare: {
    icon: Monitor,
    title: "dialogs.requestScreenShareAccess",
    need: "dialogs.needPermissionToShare",
    waiting: "dialogs.waitingForHostApproval",
    granted: "dialogs.canNowShareScreen",
    denied: "dialogs.requestDeniedByHost",
    start: "dialogs.startScreenShare",
  },
  document: {
    icon: Files,
    title: "dialogs.requestDocumentShareAccess",
    need: "dialogs.needPermissionToShareDocument",
    waiting: "dialogs.waitingForHostApprovalDocument",
    granted: "dialogs.canNowShareDocument",
    denied: "dialogs.requestDeniedByHostDocument",
    start: "dialogs.startDocumentShare",
  },
  whiteboard: {
    icon: Presentation,
    title: "dialogs.requestWhiteboardAccess",
    need: "dialogs.needPermissionForWhiteboard",
    waiting: "dialogs.waitingForHostApprovalWhiteboard",
    granted: "dialogs.canNowStartWhiteboard",
    denied: "dialogs.requestDeniedByHostWhiteboard",
    start: "dialogs.startWhiteboardNow",
  },
  annotation: {
    icon: PenLine,
    title: "dialogs.requestAnnotationAccess",
    need: "dialogs.needPermissionToAnnotate",
    waiting: "dialogs.waitingForHostApprovalAnnotation",
    granted: "dialogs.canNowAnnotate",
    denied: "dialogs.requestDeniedByHostAnnotation",
    start: "dialogs.startAnnotating",
  },
  video: {
    icon: Video,
    title: "dialogs.requestVideoShareAccess",
    need: "dialogs.needPermissionToShareVideo",
    waiting: "dialogs.waitingForHostApprovalVideo",
    granted: "dialogs.canNowShareVideo",
    denied: "dialogs.requestDeniedByHostVideo",
    start: "dialogs.startVideoShare",
  },
}

interface ScreenShareRequestDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onRequest: () => void
  onApproved: () => void
  onRejected?: () => void
  /**
   * The room's answer, or null while waiting.
   *
   * This dialog used to approve itself after a 3s `setTimeout` — a mock from
   * when nothing was wired, which would have told every asker they were
   * approved whether or not a moderator had even seen the request. The outcome
   * now comes from the caller: approval is a roles rebroadcast granting
   * presenter, rejection is `moderate:deny_presenter`.
   */
  outcome?: 'approved' | 'rejected' | null
  /** Defaults to screen share, which is what this flow was built for. */
  subject?: AccessRequestSubject
}

export function ScreenShareRequestDialog({
  open,
  onOpenChange,
  onRequest,
  onApproved,
  onRejected,
  outcome = null,
  subject = "screenShare",
}: ScreenShareRequestDialogProps) {
  const t = useTranslation();
  const copy = ACCESS_REQUEST_COPY[subject]
  const SubjectIcon = copy.icon
  const [state, setState] = useState<DialogState>("request")

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setState("request")
    }
  }, [open])

  // The room answered. Only meaningful once the ask has been sent — an outcome
  // arriving while the dialog sits on its first screen would be answering a
  // question nobody asked.
  useEffect(() => {
    if (!open || !outcome) return
    setState((prev) => (prev === "pending" ? outcome : prev))
  }, [open, outcome])

  const handleRequest = () => {
    setState("pending")
    onRequest()
  }

  const handleApproved = () => {
    onOpenChange(false)
    onApproved()
  }

  const handleRejected = () => {
    onRejected?.()
    onOpenChange(false)
  }

  const handleClose = () => {
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {/* REQUEST SCREEN - Initial state */}
        {state === "request" && (
          <>
            <DialogHeader className="!border-b-pannelcolor">
              <div className="mx-auto mt-8 flex h-16 w-16 items-center justify-center rounded-full bg-sky-100 animate-float">
                <SubjectIcon className="h-8 w-8 text-primary" />
              </div>
              <DialogTitle className="text-center my-1 text-lg">
                {t(copy.title)}
              </DialogTitle>
              <DialogDescription className="text-center">
                {t(copy.need)}
              </DialogDescription>
            </DialogHeader>

            <div className="mt-6 flex flex-col gap-3">
              <Button
                onClick={handleRequest}
                className="w-full"
                variant={'default'}
              >
                {/* What is being asked for, not always a screen. */}
                <SubjectIcon className="mr-2 h-4 w-4" />
                {t("dialogs.requestPermission")}
              </Button>
              <Button
                variant="outline"
                onClick={handleClose}
                className="w-full"
              >
                <X className="mr-2 h-4 w-4" />
                {t("dialogs.cancel")}
              </Button>
            </div>
          </>
        )}

        {/* PENDING SCREEN - Waiting for approval */}
        {state === "pending" && (
          <>
            <DialogHeader className="!border-b-pannelcolor">
              <div className="mx-auto mt-8 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 animate-float">
                <Clock className="h-8 w-8 text-amber-600 animate-pulse" />
              </div>
              <DialogTitle className="text-center text-lg">
                {t("dialogs.requestSent")}
              </DialogTitle>
              <DialogDescription className="text-center">
                {t(copy.waiting)}
              </DialogDescription>
            </DialogHeader>

            <div className="mt-6 space-y-4">
              <div className="rounded-lg bg-slate-50 dark:bg-muted p-4 text-center">
                <Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin text-sky-600" />
                <p className="text-sm text-slate-600 dark:text-muted-foreground">
                  {t("dialogs.requestSentToHost")}
                </p>
              </div>

            </div>
          </>
        )}

        {/* APPROVED SCREEN - Success */}
        {state === "approved" && (
          <>
            <DialogHeader className="!border-b-pannelcolor">
              <div className="mx-auto mt-8 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 animate-float">
                <Check className="h-8 w-8 text-emerald-600" />
              </div>
              <DialogTitle className="text-center text-lg">
                {t("dialogs.requestApproved")}
              </DialogTitle>
              <DialogDescription className="text-center">
                {t(copy.granted)}
              </DialogDescription>
            </DialogHeader>

            <div className="mt-6">
              <Button
                onClick={handleApproved}
                className="w-full"
              >
                <SubjectIcon className="mr-2 h-4 w-4" />
                {t(copy.start)}
              </Button>
            </div>
          </>
        )}

        {/* REJECTED SCREEN - Denied */}
        {state === "rejected" && (
          <>
            <DialogHeader>
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                <X className="h-8 w-8 text-red-600" />
              </div>
              <DialogTitle className="text-center text-lg">
                {t("dialogs.requestDenied")}
              </DialogTitle>
              <DialogDescription className="text-center">
                {t(copy.denied)}
              </DialogDescription>
            </DialogHeader>
          </>
        )}
      </DialogContent>
    </Dialog>
  )

}
